import type { AdminPermission } from "./teamRoles";

export enum AttributeBranchType {
  CUISINE = "CUISINE",
  TAG = "TAG",
  DRESS_CODE = "DRESS_CODE",
  AMBIANCE = "AMBIANCE",
  PARKING_OPTIONS = "PARKING_OPTIONS",
  MOMENT = "MOMENT",
}

export enum OptionType {
  Cities = "cities",
  Countries = "countries",
  Currencies = "currencies",
  Groups = "groups",
  Brands = "brands",
  BrandsActive = "brands-active",
  Branches = "branches",
  BranchesActive = "branches-active",
  LegalEntities = "legal-entities",
  LegalEntityWithHierarchy = "legal-entities-with-hierarchy",
  LegalEntitiesWithoutParent = "legal-entities-without-parent",
  Cuisines = "cuisines",
  Diets = "diets",
  Allergies = "allergies",
  MielTime = "meal-times",
  Tags = "tags",
  Occasions = "occasions",
  Ambiance = "ambiance",
  Cuisine = "cuisine",
  FlagsAndStatuses = "flag-and-status",
  CuisinesMenu = "cuisines-menu",
  DressCode = "dress-code",
  Amenity = "amenity",
  Moment = "moment",
  TransactionsGroupBy = "transactions-group-by",
  OptionsType = "property-types",
  DinerStatus = "diner-status",
  DineOutTimes = "dine-out-times",
  ServiceType = "service-type",
  TablesQrCode = "tables-qr-code",
  SocialMediaTypes = "social-media-types",
  CollectionTypes = "collections-types",
  CollectionGroupBy = "collections-group-by",
  CollectionFilters = "collections-filters",
  FavouriteRestaurantsTypes = "favorite-restaurant-type",
  OrdersOperationType = "orders-operation-type",
  referralRuleType = "referral-rule-type",
  ReferralInviteLimitType = "referral-invite-limit-type",
  PartnerRoles = "partner-roles",
  PartnerStatuses = "partner-status",
  PartnerAssignmentsLevel = "partner-assignment-level",
  AccessSections = "access-sections",
  AdminAccessSections = "admin-access-sections",
  AdminAuditEntityTypes = "audit-entity-types",
  Team = "team",
  Partners = "partners",
}

export interface OptionItem {
  id: string;
  name: string;
  group?: string;
}

/**
 * A currency from the options endpoint. Carries more than a plain option
 * because screens need to render an amount, not just pick from a list.
 */
export interface CurrencyOptionItem extends OptionItem {
  /** ISO code, e.g. "SAR" or "JOD". Same value as `id`. */
  code: string;
  /** Decimal places this currency is written with — 2 for SAR, 3 for JOD. */
  decimals: number;
  /** Symbol to show beside an amount, already resolved for the language. */
  symbol: string;
}

export interface ResetOptionItem {
  id: string | null;
  name: string | null;
}

export interface OptionItemCountry extends OptionItem {
  code: string;
  phoneCode: string | null;
}

export interface OptionItemWithChildren {
  id: string;
  name: string;
  children?: OptionItemWithChildren[];
}

export interface FilterCollectionType {
  id: string;
  type: string;
  group?: string;
  values: number[] | OptionItem[];
}

export type AmenityGroup = {
  name: string;
  values: OptionItem[];
};

export enum CollectionType {
  SELECTABLE_GROUP = "SELECTABLE_GROUP",
  RESTUARANTS_LIST = "RESTUARANTS_LIST",
  REELS = "REELS",
}

export const COLLECTION_TYPE_LABEL: Record<CollectionType, string> = {
  [CollectionType.SELECTABLE_GROUP]: "cards",
  [CollectionType.RESTUARANTS_LIST]: "list",
  [CollectionType.REELS]: "reels",
};

export enum CollectionGroupByEnum {
  CUISINE = "property.cuisine",
  MOMENT = "property.moment",
  AMBIANCE = "property.ambiance",
}

export const COLLECTION_GROUP_BY_LABEL: Record<CollectionGroupByEnum, string> =
  {
    [CollectionGroupByEnum.CUISINE]: "Cuisine",
    [CollectionGroupByEnum.MOMENT]: "Moments",
    [CollectionGroupByEnum.AMBIANCE]: "Ambiance",
  };

export enum CollectionFilterIdEnum {
  CITIES = "cities",
  AREAS = "areas",
  STREETS = "streets",
  LANDMARKS = "landmarks",
  BRANCH_TAGS = "property.branchTags",

  PRICE_LEVEL = "priceLevel",
  RATING = "rating",
  DISTANCE = "distance",

  PROPERTY_AMBIANCE = "property.ambiance",
  PROPERTY_CUISINE = "property.cuisine",
  PROPERTY_MOMENT = "property.moment",
}

export const COLLECTION_FILTER_LABEL: Record<CollectionFilterIdEnum, string> = {
  [CollectionFilterIdEnum.CITIES]: "City",
  [CollectionFilterIdEnum.AREAS]: "Area",
  [CollectionFilterIdEnum.STREETS]: "Street",
  [CollectionFilterIdEnum.LANDMARKS]: "Landmark",
  [CollectionFilterIdEnum.BRANCH_TAGS]: "Branch Tags",

  [CollectionFilterIdEnum.PRICE_LEVEL]: "Price level",
  [CollectionFilterIdEnum.RATING]: "Rating",
  [CollectionFilterIdEnum.DISTANCE]: "Distance",

  [CollectionFilterIdEnum.PROPERTY_AMBIANCE]: "Ambiance",
  [CollectionFilterIdEnum.PROPERTY_CUISINE]: "Cuisine",
  [CollectionFilterIdEnum.PROPERTY_MOMENT]: "Moments",
};

type GroupedCollection = {
  id: string;
  name: string;
  country: string;
  currency: string;
};

export interface OptionsResponse {
  cities?: OptionItem[];
  countries?: OptionItemCountry[];
  currencies?: CurrencyOptionItem[];
  legalEntities?: OptionItem[];
  legalEntitiesWithHierarchy?: OptionItemWithChildren[];
  legalEntitiesWithoutParent: OptionItem[];
  groups: GroupedCollection[];
  brands: OptionItem[];
  branches: OptionItem[];
  cuisines: OptionItem[];
  cuisinesMenu: OptionItem[];
  diets: OptionItem[];
  allergies: OptionItem[];
  mealTimes: OptionItem[];
  occasions: OptionItem[];
  flagsAndStatuses: OptionItem[];
  tags: OptionItem[];
  ambiance: OptionItem[];
  dressCode: OptionItem[];
  amenity: AmenityGroup[];
  moment: OptionItem[];
  propertyTypes: string[];
  transactionsGroupBy: string[];
  dinerStatus: string[];
  serviceType: string[];
  tablesQrCode: string[];
  dineOutTimes: string[];
  socialMediaTypes: string[];
  collectionTypes: CollectionType[];
  collectionGroupBy: CollectionGroupByEnum[];
  collectionFilters: FilterCollectionType[];
  ordersOperationType: string[];
  favoriteRestaurantType: string[];
  referralRuleType: string[];
  referralInviteLimitType: string[];
  partnerRoles: string[];
  partnerStatus: string[];
  partnerAssignmentLevel: string[];
  accessSections: {
    key: string;
    label: string;
    actions: string[];
  }[];
  adminAccessSections: {
    key: string;
    label: string;
    actions: string[];
    section: string;
  }[];
  auditEntityTypes: string[];
  team: OptionItem[];
  partners: OptionItem[];
}

export enum Status {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

export interface MediaItemDto {
  type: string;
  url: string;
}

export interface IAdminAssignRoles {
  accessRole: {
    id: string;
    name: string;
  };
  permissions: AdminPermission[];
}
